import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function ServicesList() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const services = [
    {
      id: 'housing',
      number: '01',
      icon: 'ri-home-4-line',
      title: t('sl.s1.title'),
      desc: t('sl.s1.desc'),
      details: [t('sl.s1.d1'), t('sl.s1.d2'), t('sl.s1.d3'), t('sl.s1.d4'), t('sl.s1.d5')],
      image: 'https://public.readdy.ai/ai/img_res/24c3505b14ee7ba17c3a9777fb26af68.jpg',
      alt: 'Modern apartment in Yerevan',
      reverse: false,
      cta: t('sl.s1.cta'),
    },
    {
      id: 'office',
      number: '02',
      icon: 'ri-building-office-line',
      title: t('sl.s2.title'),
      desc: t('sl.s2.desc'),
      details: [t('sl.s2.d1'), t('sl.s2.d2'), t('sl.s2.d3'), t('sl.s2.d4'), t('sl.s2.d5')],
      image: 'https://readdy.ai/api/search-image?query=modern%20professional%20office%20space%20Armenia%20Yerevan%20clean%20minimal%20design%20bright%20open%20plan%20contemporary%20business%20interior%20elegant%20workstations%20premium%20quality&width=700&height=520&seq=service-office-v2&orientation=landscape',
      alt: 'Professional office space in Yerevan',
      reverse: true,
      cta: t('sl.s2.cta'),
    },
    {
      id: 'registration',
      number: '03',
      icon: 'ri-registered-line',
      title: t('sl.s3.title'),
      desc: t('sl.s3.desc'),
      details: [t('sl.s3.d1'), t('sl.s3.d2'), t('sl.s3.d3'), t('sl.s3.d4'), t('sl.s3.d5')],
      image: 'https://readdy.ai/api/search-image?query=business%20registration%20official%20documents%20signing%20pen%20corporate%20papers%20professional%20clean%20desk%20Armenia%20Yerevan%20formal%20setup%20minimal%20natural%20light&width=700&height=520&seq=service-reg-v2&orientation=landscape',
      alt: 'Business registration documents',
      reverse: false,
      cta: t('sl.s3.cta'),
    },
    {
      id: 'legal',
      number: '04',
      icon: 'ri-scales-3-line',
      title: t('sl.s4.title'),
      desc: t('sl.s4.desc'),
      details: [t('sl.s4.d1'), t('sl.s4.d2'), t('sl.s4.d3'), t('sl.s4.d4'), t('sl.s4.d5')],
      image: 'https://readdy.ai/api/search-image?query=legal%20professional%20attorney%20office%20books%20documents%20clean%20modern%20desk%20Armenia%20professional%20corporate%20formal%20minimal%20interior%20natural%20light&width=700&height=520&seq=service-legal-v2&orientation=landscape',
      alt: 'Legal support office',
      reverse: true,
      cta: t('sl.s4.cta'),
    },
    {
      id: 'accounting',
      number: '05',
      icon: 'ri-calculator-line',
      title: t('sl.s5.title'),
      desc: t('sl.s5.desc'),
      details: [t('sl.s5.d1'), t('sl.s5.d2'), t('sl.s5.d3'), t('sl.s5.d4'), t('sl.s5.d5')],
      image: 'https://readdy.ai/api/search-image?query=professional%20accounting%20financial%20reports%20calculator%20documents%20desk%20clean%20modern%20office%20minimal%20white%20corporate%20business%20natural%20daylight%20Armenia&width=700&height=520&seq=service-accounting-v2&orientation=landscape',
      alt: 'Accounting and financial support',
      reverse: false,
      cta: t('sl.s5.cta'),
    },
    {
      id: 'residence',
      number: '06',
      icon: 'ri-passport-line',
      title: t('sl.s6.title'),
      desc: t('sl.s6.desc'),
      details: [t('sl.s6.d1'), t('sl.s6.d2'), t('sl.s6.d3'), t('sl.s6.d4'), t('sl.s6.d5')],
      image: 'https://readdy.ai/api/search-image?query=passport%20immigration%20residence%20permit%20documents%20travel%20official%20papers%20clean%20desk%20professional%20minimal%20formal%20Armenia%20legal%20documentation&width=700&height=520&seq=service-permit-v2&orientation=landscape',
      alt: 'Residence permit documents',
      reverse: true,
      cta: t('sl.s6.cta'),
    },
  ];

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col gap-20">
          {services.map((service) => (
            <div
              key={service.id}
              className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
            >
              {/* Image */}
              <div className={service.reverse ? 'lg:order-2' : ''}>
                <div className="rounded-2xl overflow-hidden w-full" style={{ height: 380 }}>
                  <img
                    src={service.image}
                    alt={service.alt}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Content */}
              <div className={service.reverse ? 'lg:order-1' : ''}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs font-bold text-gray-300 tracking-widest">{service.number}</span>
                  <div className="h-px flex-1 bg-gray-100"></div>
                </div>
                <div className="w-11 h-11 flex items-center justify-center bg-blue-50 rounded-xl mb-4">
                  <i className={`${service.icon} text-blue-600 text-xl`}></i>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {service.title}
                </h2>
                <p className="text-gray-500 text-sm leading-relaxed mb-6">{service.desc}</p>
                <ul className="flex flex-col gap-2.5 mb-8">
                  {service.details.map((detail, di) => (
                    <li key={di} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-blue-600 text-sm"></i>
                      </div>
                      <span className="text-sm text-gray-600">{detail}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => navigate('/contact')}
                  className="whitespace-nowrap inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-6 py-3 rounded-full transition-all duration-200 cursor-pointer"
                >
                  {service.cta} <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
